import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import apiClient, { setWorkspaceMonitoring } from "../api/client";
import { useRoles } from "../auth/access";
import { Workspace } from "../types";
import DataTable from "../components/DataTable";
import ExportButton from "../components/ExportButton";
import RegionBadge from "../components/RegionBadge";
import { isPersonalWorkspace } from "../components/WorkspaceCombobox";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime } from "../utils/datetime";

// ---------------------------------------------------------------------------
// Monitoring toggle cell
// ---------------------------------------------------------------------------

interface MonitoringToggleProps {
  workspace: Workspace;
  onToggle: (workspaceId: string, monitored: boolean) => Promise<void>;
}

function MonitoringToggle({ workspace, onToggle }: MonitoringToggleProps) {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [loading, setLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (loading) return;
    setLoading(true);
    try {
      await onToggle(workspace.workspaceId, !workspace.monitored);
    } finally {
      setLoading(false);
    }
  };

  const isMonitored = !!workspace.monitored;
  const tooltipLines: string[] = [];
  if (isMonitored) {
    if (workspace.monitoredBy) tooltipLines.push(`By: ${workspace.monitoredBy}`);
    if (workspace.monitoredSince) tooltipLines.push(`Since: ${formatDate(workspace.monitoredSince)}`);
  }
  const tooltip = tooltipLines.join("\n");

  return (
    <div
      className="flex flex-col items-start gap-0.5"
      title={tooltip || undefined}
    >
      <button
        onClick={handleClick}
        disabled={loading}
        aria-label={isMonitored ? "Disable monitoring" : "Enable monitoring"}
        className={[
          "relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent",
          "transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1",
          isMonitored ? "bg-green-500" : "bg-gray-300",
          loading ? "opacity-50 cursor-not-allowed" : "",
        ].join(" ")}
      >
        {loading ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <svg className="w-3 h-3 animate-spin text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          </span>
        ) : (
          <span
            className={[
              "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200",
              isMonitored ? "translate-x-4" : "translate-x-0",
            ].join(" ")}
          />
        )}
      </button>
      {isMonitored && workspace.monitoredBy && (
        <span className="text-xs text-gray-400 leading-none max-w-[120px] truncate">
          {workspace.monitoredBy}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function WorkspacesPage() {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [monitoredOnly, setMonitoredOnly] = useState(false);
  // Personal ("My workspace") workspaces are noise for SOX reviews — hidden by default.
  const [hidePersonal, setHidePersonal] = useState(true);
  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [bulkConfirm, setBulkConfirm] = useState<"monitor" | "unmonitor" | null>(null);
  const [bulkInProgress, setBulkInProgress] = useState(false);
  const navigate = useNavigate();
  const { isAdmin, isMon } = useRoles();

  const fetchWorkspaces = useCallback((filteredOnly: boolean) => {
    setLoading(true);
    setError(null);
    // include_personal=true: the API excludes personal workspaces by default,
    // but this page filters client-side so its toggle can reveal them on demand.
    const params: Record<string, string> = { include_personal: "true" };
    if (filteredOnly) params.monitored_only = "true";
    apiClient
      .get<Workspace[]>("/api/workspaces", { params })
      .then((res) => setWorkspaces(res.data))
      .catch((err) => setError(err.message ?? "Failed to load workspaces."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchWorkspaces(monitoredOnly);
  }, [fetchWorkspaces, monitoredOnly]);

  const handleToggle = useCallback(
    async (workspaceId: string, monitored: boolean) => {
      const updated = await setWorkspaceMonitoring(workspaceId, monitored);
      setWorkspaces((prev) =>
        prev.map((ws) => (ws.workspaceId === workspaceId ? { ...ws, ...updated } : ws))
      );
    },
    []
  );

  const personalCount = workspaces.filter(isPersonalWorkspace).length;
  const afterPersonal = hidePersonal
    ? workspaces.filter((ws) => !isPersonalWorkspace(ws))
    : workspaces;

  // Region options come from the data rather than a fixed list — a workspace
  // moved onto a capacity in a new geography should appear here on its own.
  const regionOptions = Array.from(
    new Set(afterPersonal.map((ws) => ws.region || "").filter(Boolean))
  ).sort();

  const base = regionFilter
    ? afterPersonal.filter((ws) => (ws.region || "") === regionFilter)
    : afterPersonal;

  const filtered = search.trim()
    ? base.filter((ws) =>
        ws.name?.toLowerCase().includes(search.trim().toLowerCase()) ||
        ws.workspaceId?.toLowerCase().includes(search.trim().toLowerCase())
      )
    : base;

  const monitoredCount = base.filter((ws) => ws.monitored).length;

  // The rows on screen, and the only rows a bulk action may touch.
  //
  // This read `workspaces` with the personal filter applied and nothing else,
  // so it ignored both the region filter and the search box while the
  // confirmation directly above it counted the region-filtered set. Selecting
  // Region = Southeast Asia showed nineteen rows, the prompt offered to
  // monitor "all 19 workspaces", and confirming enabled monitoring on all 135
  // non-personal workspaces in the tenant — seven times what it asked for, in
  // every geography, with no way to tell afterwards which ones you had meant.
  //
  // Declared here rather than inside the handler so the count in the prompt
  // and the set that gets written are the same expression, and cannot drift
  // apart again.
  const bulkTargets = filtered;

  const handleBulkConfirm = async (action: "monitor" | "unmonitor") => {
    setBulkInProgress(true);
    setBulkConfirm(null);
    for (const ws of bulkTargets) {
      const desired = action === "monitor";
      if (ws.monitored !== desired) {
        try {
          const updated = await setWorkspaceMonitoring(ws.workspaceId, desired);
          setWorkspaces((prev) =>
            prev.map((w) => (w.workspaceId === ws.workspaceId ? { ...w, ...updated } : w))
          );
        } catch {
          // continue on individual failure
        }
      }
    }
    setBulkInProgress(false);
  };

  const buildColumns = (): ColumnDef<Workspace, any>[] => [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "type", header: "Type", cell: ({ getValue }) => getValue() || "—" },
    {
      accessorKey: "state",
      header: "State",
      cell: ({ getValue }) => {
        const v = getValue<string>();
        if (!v) return "—";
        const colour =
          v === "Active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600";
        return (
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${colour}`}>
            {v}
          </span>
        );
      },
    },
    {
      id: "region",
      header: "Region",
      accessorFn: (row) => row.region || "",
      cell: ({ row }) => (
        <RegionBadge
          region={row.original.region}
          source={row.original.regionSource}
          capacityName={row.original.capacityName}
          capacitySku={row.original.capacitySku}
        />
      ),
    },
    {
      id: "capacity",
      header: "Capacity",
      accessorFn: (row) => row.capacityName || row.capacityId || "",
      cell: ({ row }) => {
        const { capacityName, capacitySku, capacityId } = row.original;
        // Before capacities were collected this column showed a bare GUID,
        // which identified nothing to a human reader.
        if (capacityName) {
          return (
            <span className="text-xs" title={capacityId}>
              {capacityName}
              {capacitySku && <span className="ml-1 text-content-muted">({capacitySku})</span>}
            </span>
          );
        }
        if (capacityId) return <span className="font-mono text-xs">{capacityId}</span>;
        return <span className="text-xs text-content-muted">Shared</span>;
      },
    },
    {
      accessorKey: "reportCount",
      header: "Reports",
      cell: ({ getValue }) => (
        <span className="font-mono">{getValue<number>() ?? 0}</span>
      ),
    },
    { accessorKey: "lastSyncedAt", header: "Last Synced", cell: ({ getValue }) => formatDate(getValue()) },
    {
      id: "git",
      header: "Git",
      accessorFn: (row) => row.gitIntegration,
      cell: ({ getValue }) => {
        const git = getValue<Workspace["gitIntegration"]>();
        if (!git) return null;
        const icon = (
          <svg
            className="w-4 h-4 text-gray-600"
            fill="currentColor"
            viewBox="0 0 24 24"
            aria-label="Git connected"
          >
            <path d="M2.6 10.59L8.38 4.8l1.69 1.7c-.24.85.15 1.78.93 2.23v5.54c-.6.34-1 .99-1 1.73a2 2 0 0 0 2 2 2 2 0 0 0 2-2c0-.74-.4-1.39-1-1.73V9.41l2.07 2.09c-.07.15-.07.32-.07.5a2 2 0 0 0 2 2 2 2 0 0 0 2-2 2 2 0 0 0-2-2c-.2 0-.37.03-.54.09L11.06 6.1a2 2 0 0 0-1.56-2.05 2 2 0 0 0-2.42 1.44 2 2 0 0 0 .32 1.65L1.6 12.96l1 1.01L2.6 10.59z" />
          </svg>
        );
        return git.repoUrl ? (
          <a
            href={git.repoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:underline"
            title={`${git.provider} — ${git.repoUrl} (${git.branch})`}
            onClick={(e) => e.stopPropagation()}
          >
            {icon}
          </a>
        ) : (
          <span title={`${git.provider} (${git.branch})`} className="inline-flex items-center">
            {icon}
          </span>
        );
      },
    },
    // Dropped for the monitored-only role, whose every row would read
    // "Monitored" — a column with one value in it says nothing.
    ...(isMon ? [] : [{
      id: "monitoring",
      header: "Monitoring",
      cell: ({ row }: { row: { original: Workspace } }) =>
        isAdmin ? (
          <MonitoringToggle workspace={row.original} onToggle={handleToggle} />
        ) : (
          // Whether a workspace is monitored is worth seeing at a glance;
          // changing it is an Admin action, so other roles get a label.
          <span
            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
              row.original.monitored
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {row.original.monitored ? "Monitored" : "Not monitored"}
          </span>
        ),
    } as ColumnDef<Workspace, any>]),
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workspaces</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isMon
              ? // Saying "all" would be wrong for a role the API filters, and
                // a reader who cannot see a workspace they know exists needs
                // to be told why rather than left to assume data is missing.
                "The Power BI workspaces monitored for SOX compliance. Your role covers these only."
              : "All Power BI workspaces monitored for SOX compliance."}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* The export endpoint has no monitored-only variant, so for that
              role the button could only ever 403. */}
          {!isMon && (
            <ExportButton endpoint="/api/export/workspaces" filename="workspaces.csv" />
          )}
        </div>
      </div>

      {/* Controls row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search workspaces…"
            className="pl-8 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        {/* Region filter */}
        {regionOptions.length > 1 && (
          <label className="flex items-center gap-2 text-sm text-content-muted">
            Region
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="rounded-md border border-edge bg-surface-raised px-2 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="">All regions</option>
              {regionOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Monitored only toggle. Hidden for the monitored-only role: the API
            already filters its list, so the toggle would appear to do nothing
            whichever way it was set. */}
        {!isMon && (
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <button
            onClick={() => setMonitoredOnly((v) => !v)}
            className={[
              "relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent",
              "transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
              monitoredOnly ? "bg-blue-600" : "bg-gray-300",
            ].join(" ")}
          >
            <span
              className={[
                "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200",
                monitoredOnly ? "translate-x-4" : "translate-x-0",
              ].join(" ")}
            />
          </button>
          <span className="text-sm text-gray-700">Monitored only</span>
        </label>
        )}

        {/* Hide personal workspaces toggle */}
        <label
          className="flex items-center gap-2 cursor-pointer select-none"
          title="Personal ('My workspace') workspaces are hidden by default"
        >
          <button
            role="switch"
            aria-checked={hidePersonal}
            onClick={() => setHidePersonal((v) => !v)}
            className={[
              "relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent",
              "transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
              hidePersonal ? "bg-blue-600" : "bg-gray-300",
            ].join(" ")}
          >
            <span
              className={[
                "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200",
                hidePersonal ? "translate-x-4" : "translate-x-0",
              ].join(" ")}
            />
          </button>
          <span className="text-sm text-gray-700">
            Hide personal
            {hidePersonal && personalCount > 0 && (
              <span className="ml-1.5 text-xs text-gray-400">({personalCount} hidden)</span>
            )}
          </span>
        </label>

        {/* Bulk actions. Admin only: monitoring decides which workspaces
            produce SOX evidence, so the API refuses it for other roles and the
            buttons would only fail. */}
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setBulkConfirm("monitor")}
              disabled={bulkInProgress || loading}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-green-300 text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50"
            >
              Monitor All
            </button>
            <button
              onClick={() => setBulkConfirm("unmonitor")}
              disabled={bulkInProgress || loading}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Unmonitor All
            </button>
          </div>
        )}
      </div>

      {/* Bulk confirmation dialog */}
      {bulkConfirm && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-yellow-800">
            {bulkConfirm === "monitor" ? "Enable" : "Disable"} monitoring for the{" "}
            {bulkTargets.length} workspace{bulkTargets.length !== 1 ? "s" : ""} listed
            below
            {/* Naming the filters, not just the count. "All 19 workspaces" is
                ambiguous between the current view and the tenant, and this
                action used to mean the second while showing the first. */}
            {regionFilter && <> in {regionFilter}</>}
            {search.trim() && <> matching “{search.trim()}”</>}?
            {hidePersonal && personalCount > 0 && (
              <span className="text-yellow-600"> Hidden personal workspaces are not affected.</span>
            )}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => handleBulkConfirm(bulkConfirm)}
              className="px-3 py-1 text-xs font-medium rounded bg-yellow-600 text-white hover:bg-yellow-700"
            >
              Confirm
            </button>
            <button
              onClick={() => setBulkConfirm(null)}
              className="px-3 py-1 text-xs font-medium rounded bg-white border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {bulkInProgress && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Applying bulk monitoring change…
        </div>
      )}

      {/* Content */}
      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading workspaces…
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-500">
            {search.trim() ? (
              <>
                <span className="font-medium text-gray-700">{filtered.length}</span> of{" "}
                <span className="font-medium text-gray-700">{base.length}</span> workspace{base.length !== 1 ? "s" : ""} match
                {" "}· <span className="font-medium text-gray-700">{monitoredCount}</span> monitored.
              </>
            ) : (
              <>
                <span className="font-medium text-gray-700">{monitoredCount} of {base.length}</span>{" "}
                workspace{base.length !== 1 ? "s" : ""} monitored.
              </>
            )}
            {hidePersonal && personalCount > 0 && (
              <> · {personalCount} personal workspace{personalCount !== 1 ? "s" : ""} hidden.</>
            )}{" "}
            Click a row to view its reports.
          </p>
          <DataTable
            data={filtered}
            columns={buildColumns()}
            onRowClick={(ws) => navigate(`/workspaces/${ws.workspaceId}`)}
          />
        </>
      )}
    </div>
  );
}
